/**
 * @file use-category-form-image.ts
 *
 * Manages the category form's single-image upload: the in-flight/error state and
 * the drop-zone callbacks. On a successful upload it writes the URL and the real
 * `media_assets` id back into the form via the provided `patch`; rejections from the
 * drop zone are mapped to localized messages. Kept separate from the view (mirroring
 * the products tab's `use-product-form-media`) so the upload concern is isolated and
 * unit-testable. The drop-zone upload uses the real `/v1` webspace endpoint; the
 * media-library path stays on mock data for now (its ids aren't `media_assets` UUIDs).
 */

import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { uploadImage } from '../../api/media';
import { MAX_MEDIA_FILE_SIZE_MB } from '../../products/form/product-form-constants';
import type { MediaErrorCode } from '../../components/MediaDropZone';
import type { CategoryFormFields } from './category-form-types';

/** What {@link useCategoryFormImage} returns to the form. */
export interface UseCategoryFormImageResult {
  uploading: boolean;
  imageError?: string;
  handleFiles: (files: File[]) => void;
  handleError: (codes: MediaErrorCode[]) => void;
  selectFromLibrary: (item: { url: string; id: string }) => void;
  removeImage: () => void;
}

/**
 * Manages the category image upload, writing results back through `patch`.
 *
 * @param patch - Updater for the form fields (sets `imageSrc`/`imageId`).
 *
 * @returns The upload state and the drop-zone/remove handlers.
 */
export function useCategoryFormImage(
  patch: (update: Partial<CategoryFormFields>) => void,
): UseCategoryFormImageResult {
  const intl = useIntl();
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState<string | undefined>(undefined);

  const handleFiles = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file || uploading) {
        return;
      }
      setUploading(true);
      setImageError(undefined);
      uploadImage(file)
        .then((media) => patch({ imageSrc: media.src, imageId: media.id }))
        .catch(() => setImageError(intl.formatMessage(adminMessages.formUploadFailed)))
        .finally(() => setUploading(false));
    },
    [uploading, patch, intl],
  );

  const handleError = useCallback(
    (codes: MediaErrorCode[]) => {
      const code = codes[0];
      const message =
        code === 'size'
          ? intl.formatMessage(adminMessages.formErrorFileTooLarge, {
              maxMb: MAX_MEDIA_FILE_SIZE_MB,
            })
          : code === 'limit'
            ? intl.formatMessage(adminMessages.formErrorImageLimit, { max: 1 })
            : intl.formatMessage(adminMessages.formErrorUnsupportedType);
      setImageError(message);
    },
    [intl],
  );

  const selectFromLibrary = useCallback(
    (item: { url: string; id: string }) => {
      patch({ imageSrc: item.url, imageId: item.id });
      setImageError(undefined);
    },
    [patch],
  );

  const removeImage = useCallback(() => {
    patch({ imageSrc: '', imageId: null });
    setImageError(undefined);
  }, [patch]);

  return { uploading, imageError, handleFiles, handleError, selectFromLibrary, removeImage };
}
